import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileClock, Mail, ListChecks, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate } from "@/lib/api";
import { useCentro } from "@/lib/centro";
import { EnviarDialog } from "@/components/InformesPdf";

const dias = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

export const InformesSinEnviar = () => {
  const { centro } = useCentro();
  const [rows, setRows] = useState(null);
  const [todas, setTodas] = useState(false);
  const [env, setEnv] = useState(null);
  const [cola, setCola] = useState({ items: [], i: 0, enviados: 0 });
  const load = useCallback(() => {
    api.get("/informes-pdf/sin-enviar", { params: { centro } }).then((r) => setRows(r.data));
  }, [centro]);
  useEffect(() => {
    load();
  }, [load]);
  const vista = todas ? rows || [] : (rows || []).slice(0, 8);
  const enCola = cola.items.length > 0;
  const empezar = (items = rows) => {
    setCola({ items, i: 0, enviados: 0 });
    setEnv(items[0]);
  };
  const atrasados = (rows || []).filter((r) => dias(r.created_at) > 7);
  const parar = (c = cola) => {
    if (c.items.length) toast.success(`Enviados ${c.enviados} de ${c.items.length} informes`);
    setCola({ items: [], i: 0, enviados: 0 });
    setEnv(null);
  };
  const siguiente = (enviado) => {
    const c = { ...cola, i: cola.i + 1, enviados: cola.enviados + (enviado ? 1 : 0) };
    if (c.i >= c.items.length) return parar(c);
    setCola(c);
    setEnv(c.items[c.i]);
  };
  const hecho = () => {
    load();
    window.dispatchEvent(new Event("informe-enviado"));
    if (enCola) siguiente(true);
    else setEnv(null);
  };
  return (
    <div className="bg-white border border-amber-200 rounded-lg p-4 fade-up" data-testid="informes-sin-enviar">
      <div className="flex items-center gap-2 mb-3">
        <FileClock className="h-5 w-5 text-amber-600" />
        <p className="text-sm font-semibold text-slate-800">Informes subidos sin enviar <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800" data-testid="informes-sin-enviar-count">{rows?.length ?? 0}</span></p>
        {rows?.length > 1 && <button type="button" onClick={() => empezar()} className="ml-auto inline-flex items-center gap-1 rounded-md bg-[#1E3A8A] px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-[#1E40AF]" data-testid="informes-enviar-seguidos"><ListChecks className="h-3.5 w-3.5" />Enviar uno tras otro ({rows.length})</button>}
      </div>
      {atrasados.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="informes-atrasados-aviso">
          <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
          <span className="flex-1"><b>{atrasados.length} {atrasados.length === 1 ? "informe lleva" : "informes llevan"} más de 7 días</b> subido{atrasados.length === 1 ? "" : "s"} sin enviarse a la familia: {atrasados.slice(0, 3).map((r) => r.paciente).join(", ")}{atrasados.length > 3 ? "…" : ""}</span>
          <button type="button" onClick={() => empezar(atrasados)} className="rounded-md bg-rose-600 px-2.5 py-1 font-medium text-white transition-colors hover:bg-rose-700" data-testid="informes-atrasados-enviar">Enviar estos ahora</button>
        </div>
      )}
      {rows && !rows.length && <p className="text-xs text-slate-400">Todos los informes subidos están enviados.</p>}
      <div className="divide-y divide-slate-100">
        {vista.map((r) => {
          const n = dias(r.created_at);
          return (
            <div key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 py-1.5 text-xs" data-testid={`informe-sin-enviar-${r.id}`}>
              <span className="w-20 font-medium text-slate-800">{fmtDate(r.created_at.slice(0, 10))}</span>
              <Link to={`/pacientes/${r.patient_id}`} className="min-w-[140px] flex-1 truncate text-[#1E3A8A] hover:underline">{r.paciente}</Link>
              <span className="max-w-[160px] truncate text-slate-400">{r.filename}{r.subido_por ? ` · ${r.subido_por}` : ""}</span>
              <span className={`rounded px-1.5 py-0.5 ${n > 7 ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{n === 0 ? "hoy" : `hace ${n} d`}</span>
              <button type="button" onClick={() => setEnv(r)} className="inline-flex items-center gap-1 rounded-md border border-[#1E3A8A]/30 px-2 py-0.5 text-[#1E3A8A] transition-colors hover:bg-blue-50" data-testid={`informe-sin-enviar-enviar-${r.id}`}><Mail className="h-3.5 w-3.5" />Enviar</button>
            </div>
          );
        })}
      </div>
      {rows?.length > 8 && <button type="button" onClick={() => setTodas(!todas)} className="mt-2 text-[11px] text-[#1E3A8A] underline" data-testid="informes-sin-enviar-todas">{todas ? "Ver menos" : `Ver los ${rows.length}`}</button>}
      {env && <EnviarDialog inf={env} patient={env.patient} paso={enCola ? `${cola.i + 1} de ${cola.items.length}` : ""} onSkip={enCola ? () => siguiente(false) : undefined} onClose={() => (enCola ? parar() : setEnv(null))} onDone={hecho} />}
    </div>
  );
};
